import React, { useEffect, useId, useState } from "react";

import { DatasetDataInfoResponse } from "api/model/dataset/DatasetDataInfoResponse";
import { InstantDoubleValue } from "api/model/dataset/InstantDoubleValue";
import { rgb, schemeCategory10 } from "d3";
import { BubbleChartDTO } from "api/model/product-template/IProductTemplateInfoDTO";
import { useComponentStateCallback } from "hooks/dashboard/useComponentStateCallback";
import useTimeState from "hooks/dashboard/useTimeState";
import Plotly, { Layout, LayoutAxis, ModeBarButton, ModeBarDefaultButtons } from "plotly.js";
import Plot from "react-plotly.js";
import { APIResponse } from "services/HttpService";
import { I, T } from "services/lang/LanguageService";
import useBubbleChartData from "api/api-hooks/bubble-chart-hooks";
import { DATE_PATTERN_SECONDS, dweenColorway } from "utils/constants";
import { ProgressSpinner } from "primereact/progressspinner";
import { fmtDatetime } from "utils/time-utils";
import { getChartSizeValue } from "utils/utils";
import { useDisplayTimezone } from "hooks/useTimezone";
import DatasetsTitlesShower from "../DatasetsTitlesShower";
import ExportCsvDialog from "../ExportCsvDialog";
import ChartWrapper from "../chart-wrapper/ChartWrapper";
import copyChartToClipBoard from "../chartUtils";
import { clipboardIcon, csvDownloadIcon, questionMarkIcon } from "../icons";
import { AnnotationConfig, AxesConfig, DatasetScatterProps, ImageConfig, PlotDataEx } from "../interfaces";

const commonAxisConfig = {
  showspikes: true,
  spikemode: "toaxis",
  automargin: true,
  tickformat: "f",
} as Partial<LayoutAxis>;

const layoutConfig = {
  legend: {
    orientation: "h",
    y: -0.3,
  },
  colorway: dweenColorway,
  margin: {
    l: 0,
    r: 0,
    b: 0,
    t: 25,
  },
} as Plotly.Layout;

const axisCommonConfig = {
  spikemode: "toaxis",
  showspikes: true,
};

export default function DatasetScatterChart(props: DatasetScatterProps) {
  const displayTz = useDisplayTimezone();

  const uniqueId = useId();

  const [axesConfig, setAxesConfig] = useState<AxesConfig>({});
  const setStatus = useComponentStateCallback(props);

  const [titlesAndOverriddenTitles, setTitlesAndOverriddenTitles] = useState<{ title: string; overriddenTitle: string }[]>([]);
  const [isOverriddenTitleExist, setIsOverriddenTitleExist] = useState<boolean>(false);
  const [titlesDialog, setTitlesDialog] = useState<boolean>(false);
  const [exportCsvDialog, setExportCsvDialog] = useState<boolean>(false);

  const {
    productTemplateId,
    templateXAttributeId,
    templateYAttributeId,
    templateSizeAttributeId,
    templateColorAttributeId,
    lastEntryCount = 10,
  } = props.productTemplate || {};

  const formatAttr = (attr?: string | number) => (attr ? `${productTemplateId}:${attr}` : undefined);

  const { data: bubbleChartData } = useBubbleChartData(
    {
      id: productTemplateId as number,
      xAttr: formatAttr(templateXAttributeId),
      yAttr: formatAttr(templateYAttributeId),
      zAttr: formatAttr(templateSizeAttributeId),
      colorAttr: formatAttr(templateColorAttributeId),
      nCount: lastEntryCount,
    },
    {
      enabled: !!props.bubbleChart && !!productTemplateId,
    }
  );

  const timeState = useTimeState({
    from: props.from,
    to: props.to,
  });

  const chartConfig: Partial<Plotly.Config> = {
    displaylogo: false,
    modeBarButtonsToRemove: ["lasso2d", "select2d"],
    modeBarButtonsToAdd: [
      {
        title: T("chart-config.copy-chart-to-clipboard"),
        name: "Copy chart to Clipboard",
        icon: clipboardIcon,
        click: copyChartToClipBoard,
      },
      {
        title: `${T("action.download")} CSV`,
        name: "Download CSV",
        icon: csvDownloadIcon,
        click: () => setExportCsvDialog(true),
      },
      isOverriddenTitleExist && {
        title: T("chart-config.show-titles"),
        name: "Show Titles",
        icon: questionMarkIcon,
        click: () => setTitlesDialog(true),
      },
    ].filter(Boolean) as ModeBarDefaultButtons[] | ModeBarButton[] | undefined,
  };

  function flattenArrayOfObjects(): string[] {
    const flattenedArray: string[] = [];

    flattenedArray.push(props.xAxisIdentifier);
    if (typeof props.yAxisIdentifier === "string") {
      flattenedArray.push(props.yAxisIdentifier);
    }
    if (Array.isArray(props.yAxisIdentifier)) {
      props.yAxisIdentifier.forEach(y => {
        if (typeof y === "string") {
          flattenedArray.push(y);
        } else if (typeof y === "object" && y.identifier) {
          flattenedArray.push(y.identifier);
        }
      });
    }
    if (props.colorAxisIdentifier) {
      flattenedArray.push(props.colorAxisIdentifier);
    }
    if (props.sizeAxisIdentifier) {
      flattenedArray.push(props.sizeAxisIdentifier);
    }
    return flattenedArray;
  }

  const linearRegression = (data: [number, number][]) => {
    const n = data.length;
    const sumX = data.reduce((sum, [x]) => sum + x, 0);
    const sumY = data.reduce((sum, [, y]) => sum + y, 0);
    const sumXY = data.reduce((sum, [x, y]) => sum + x * y, 0);
    const sumX2 = data.reduce((sum, [x]) => sum + x * x, 0);

    const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
    const intercept = (sumY - slope * sumX) / n;

    return { slope, intercept };
  };

  const linearRegressionLine = ({ slope, intercept }: { slope: number; intercept: number }) => {
    return (x: number) => slope * x + intercept;
  };

  const allSame = (arr: number[]) => arr.every(val => val === arr[0]);

  const addTrendline = (xData: number[], yData: number[], color: string, name: string, minX: number, maxX: number) => {
    if (!props.trendLines || xData.length !== yData.length || xData.length === 0 || allSame(yData)) {
      return null;
    }

    const offset = Math.min(...xData);
    const regressionData = xData.map((x, i) => [x - offset, yData[i]]) as [number, number][];
    const regressionLine = linearRegression(regressionData);
    const regression = linearRegressionLine(regressionLine);

    const xRange = [minX, maxX];
    const trendlineY = xRange.map(x => regression(x - offset));

    const formula = `y = ${regressionLine.slope.toFixed(2)} * x + ${regressionLine.intercept.toFixed(2)}`;

    return {
      x: xRange,
      y: trendlineY,
      mode: "lines",
      type: "scatter",
      name: `Trendline (${name})`,
      hovertemplate: `${formula}`,
      line: { color, width: 1, dash: "dash" },
    };
  };

  const promiseHandler = (res: APIResponse<any>) => {
    if (Array.isArray(props.yAxisIdentifier) && props.yAxisIdentifier.length > 1 && props.colorAxisIdentifier) {
      console.error("Color and Y array can't co-exist...");
      return []; // ERROR return
    }

    type PlotDataExInformation = { title: string; unit: string };
    type PlotDataExArrays = {
      x: Array<number>;
      y: Array<number>;
      customdata?: Array<string>;
      markerColors?: Array<number>;
      size?: Array<number>;
      symbols?: Array<number | string>;
    };
    function extractArraysFromApiResponse(arrays: {
      xArr: InstantDoubleValue[];
      yArr: InstantDoubleValue[];
      colorArr?: InstantDoubleValue[];
      sizeArr?: InstantDoubleValue[];
    }): PlotDataExArrays {
      const x: Array<number> = [];
      const y: Array<number> = [];
      const markerColors: Array<number> = [];
      const size: Array<number> = [];
      const symbols: Array<number> = [];
      const customdata: Array<string> = [];

      const xMap = new Map<string, number>();
      const yMap = new Map<string, number>();
      const colorMap = new Map<string, number>();
      const sizeMap = new Map<string, number>();

      const handleSetMap = (array: InstantDoubleValue[] | undefined, targetMap: Map<string, number>) => {
        array?.forEach(({ key, value }) => {
          targetMap.set(key, value);
        });
      };

      handleSetMap(arrays.xArr, xMap);
      handleSetMap(arrays.yArr, yMap);
      handleSetMap(arrays.colorArr, colorMap);
      handleSetMap(arrays.sizeArr, sizeMap);

      if (arrays.colorArr) {
        for (let i = 0; i < arrays.colorArr.length; i++) {
          const obj3 = arrays.colorArr[i];
          colorMap.set(obj3.key, obj3.value);
        }
      }

      if (arrays.sizeArr) {
        for (let i = 0; i < arrays.sizeArr.length; i++) {
          const obj4 = arrays.sizeArr[i];
          sizeMap.set(obj4.key, obj4.value);
        }
      }

      Array.from(xMap.entries()).forEach(([key, value]) => {
        if (yMap.has(key) && (!arrays.colorArr || colorMap.has(key)) && (!arrays.sizeArr || sizeMap.has(key))) {
          x.push(value);
          y.push(yMap.get(key)!);

          if (arrays.colorArr) {
            markerColors.push(colorMap.get(key)!);
          }

          if (arrays.sizeArr) {
            size.push(sizeMap.get(key)!);
          }

          customdata.push(fmtDatetime(key, displayTz, DATE_PATTERN_SECONDS));
        }
      });

      if (props.highlightCount && props.highlightCount > 0) {
        if (x.length > props.highlightCount) {
          for (let i = 0; i < x.length - props.highlightCount; i++) {
            symbols.push(0);
          }
          for (let i = 0; i < props.highlightCount; i++) {
            symbols.push(4);
          }
        } else {
          for (let i = 0; i < x.length; i++) {
            symbols.push(4);
          }
        }
      }

      return { x, y, markerColors, size, customdata, symbols };
    }

    function extractObjectByIdentifier(objects: Array<DatasetDataInfoResponse>, identifier: string) {
      for (let i = 0; i < objects.length; i++) {
        if (objects[i].identifier === identifier) {
          return objects.splice(i, 1)[0];
        }
      }

      return undefined;
    }
    function truncateMilliseconds(arr: InstantDoubleValue[]): InstantDoubleValue[] {
      return arr.map(obj => {
        const newObj: InstantDoubleValue = { key: "", value: obj.value };
        const date = new Date(obj.key);
        date.setMilliseconds(0);
        newObj.key = date.toISOString();
        return newObj;
      });
    }

    const symbolWidthLogic = (symbols?: Array<number | string>) => {
      if (symbols) {
        if (props.sizeAxisIdentifier) {
          return symbols.map(() => 1);
        }
        return symbols.map(e => (e === 0 ? 0 : 2));
      }

      return undefined;
    };

    const darkenColor = (clr: string, percentage: number = 0.25): string => {
      const { r, g, b } = rgb(clr);
      const factor = 1 - percentage;
      return `rgb(${Math.floor(r * factor)}, ${Math.floor(g * factor)}, ${Math.floor(b * factor)})`;
    };

    const createPlotDataExObject = (info: PlotDataExInformation, arrs: PlotDataExArrays, index: number): PlotDataEx => {
      const baseColor = schemeCategory10[index % 10];
      const darkerColor = darkenColor(baseColor);
      let markerColors = new Array(arrs.x.length).fill(baseColor);

      if (props.highlightCount && props.highlightCount <= markerColors.length && !props.discreteColorMode) {
        for (let i = markerColors.length - 1; i >= markerColors.length - props.highlightCount; i--) {
          markerColors[i] = darkerColor;
        }
      }

      if (
        props.colorAxisIdentifier &&
        props.discreteColorMode &&
        props.discreteValues &&
        props.discreteValues.length > 0 &&
        arrs.markerColors
      ) {
        const discreteMap = new Map(props.discreteValues.map(({ key, value, title }) => [key, { value, title }]));

        markerColors = arrs.markerColors.map(colorValue =>
          discreteMap.has(colorValue) ? discreteMap.get(colorValue)!.value : "rgba(200, 200, 200, 0.5)"
        );
      }

      return {
        name: info.title,
        type: "scatter",
        mode: "markers",
        hovertemplate: `<b>X:</b> %{x:.3f} <b>Y:</b> %{y:.3f} <br><b>${T("word.date")}:</b> %{customdata}<extra></extra> <br><b>${info.title}:</b>`,
        unit: info.unit,
        marker: {
          color:
            arrs.markerColors && arrs.markerColors.length > 0 && !props.discreteColorMode ? arrs.markerColors : markerColors,
          showscale: !!(arrs.markerColors && arrs.markerColors.length > 0) && !props.discreteColorMode,
          size: arrs.size && arrs.size.length > 0 ? arrs.size : undefined,
          symbol: arrs.symbols && arrs.symbols.length > 0 ? arrs.symbols : undefined,
          line: {
            color: arrs.symbols ? arrs.symbols.map((e, i) => (e === 0 ? "white" : darkenColor(markerColors[i]))) : undefined,
            width: symbolWidthLogic(arrs.symbols),
          },
        },
        showlegend: !props.discreteColorMode || !props.colorAxisIdentifier,
        x: arrs.x,
        y: arrs.y,
        customdata: arrs.customdata,
      } as PlotDataEx;
    };

    // removes milliseconds for all returned data
    res.data = res.data.map((ds: DatasetDataInfoResponse) => {
      return { ...ds, data: truncateMilliseconds(ds.data) };
    });

    const xObject = extractObjectByIdentifier(res.data, props.xAxisIdentifier);

    if (!xObject) {
      console.error("X dataset is not defined...");
      return [];
    }

    let colorObject: DatasetDataInfoResponse | undefined;
    if (props.colorAxisIdentifier) {
      colorObject = extractObjectByIdentifier(res.data, props.colorAxisIdentifier);
    }

    let sizeObject: DatasetDataInfoResponse | undefined;
    if (props.sizeAxisIdentifier) {
      sizeObject = extractObjectByIdentifier(res.data, props.sizeAxisIdentifier);
    }
    if (res.data.length === 0) {
      console.error("Y dataset(s) is not defined");
      return [];
    }

    const checkUnits = () => {
      const { unit } = res.data[0];

      res.data.forEach((ob: DatasetDataInfoResponse) => {
        if (ob.unit !== unit) {
          return "ERROR";
        }
        return undefined;
      });

      return unit;
    };
    createAxesConfig(xObject.unit as string, checkUnits());

    let maxX = -Infinity;
    let minX = Infinity;

    xObject.data.forEach((point: InstantDoubleValue) => {
      if (minX > point.value) {
        minX = point.value;
      }
      if (maxX < point.value) {
        maxX = point.value;
      }
    });

    const datasets = res.data.map((ds: DatasetDataInfoResponse, index: number) => {
      const overriddenTitle = Array.isArray(props.yAxisIdentifier)
        ? (
            props.yAxisIdentifier.find(item => typeof item !== "string" && item.identifier === ds.identifier) as {
              overriddenTitle?: string;
            }
          )?.overriddenTitle || ""
        : "";
      const plotData = createPlotDataExObject(
        {
          title: overriddenTitle || ds.title,
          unit: ds.unit as string,
        },
        extractArraysFromApiResponse({
          xArr: xObject.data,
          yArr: ds.data,
          colorArr: colorObject?.data,
          sizeArr: sizeObject?.data,
        }),
        index
      );

      const color = schemeCategory10[index % 10];

      const xValues = plotData.x as number[];
      const yValues = plotData.y as number[];
      const trendlineData = addTrendline(xValues, yValues, color, ds.title, minX, maxX);

      return [plotData, trendlineData].filter(Boolean);
    });

    setTitlesAndOverriddenTitles(datasets.map((d: { name: string }[]) => ({ title: d[0].name, overriddenTitle: d[0].name })));

    if (props.highlightCount && props.highlightCount > 0) {
      const highlightLegendTrace: Partial<PlotDataEx> = {
        x: [null],
        y: [null],
        mode: "markers",
        marker: {
          symbol: 4,
          size: 10,
          color: "black",
        },
        name: props.highlightedValuesDescription ?? "Highlighted values",
        showlegend: true,
        hoverinfo: "skip",
      };

      datasets.push(highlightLegendTrace);
    }
    return datasets.flat();
  };

  useEffect(() => {
    const hasOverriddenTitle = titlesAndOverriddenTitles.some(dataset => dataset.overriddenTitle !== "");
    setIsOverriddenTitleExist(hasOverriddenTitle);
  }, [titlesAndOverriddenTitles]);

  const createPhotoConfig = (photo?: ImageConfig) => {
    if (!photo || !photo.source) return {};

    const { source, x = 0, y = 0, sizex = 1, sizey = 1, opacity = 1 } = photo;

    return { source, x, y, sizex, sizey, opacity };
  };

  const createAnnotationsConfig = (annotations?: Array<AnnotationConfig>) => {
    if (!annotations) return [];

    return annotations.map((annotation: AnnotationConfig) => {
      return { showarrow: false, ...annotation } as Partial<Plotly.Annotations>;
    });
  };

  const createAxesConfig = (xUnit: string, yUnit: string) => {
    const axes = axesConfig;
    axes.xaxis = {
      title: {
        text: props.bubbleChart ? bubbleChartData?.xAxisName : xUnit,
        standoff: 10,
      },
      range: props.axisConfig?.xRange,
      ...axisCommonConfig,
    } as LayoutAxis;

    axes.yaxis = {
      title: {
        text: props.bubbleChart ? bubbleChartData?.yAxisName : yUnit,
        standoff: 10,
      },
      range: props.axisConfig?.yRange,
      ...commonAxisConfig,
    } as LayoutAxis;
    setAxesConfig({ ...axes });
  };

  if (Array.isArray(props.yAxisIdentifier) && props.yAxisIdentifier.length > 1 && props.colorAxisIdentifier) {
    return <div className="error-div">{T("error.config-error")}</div>;
  }

  const handleBubbleChartData = (data: BubbleChartDTO): Plotly.Data[] => {
    const traces: Plotly.Data[] = [];

    data.traces?.forEach((trace, index) => {
      const bubbleTrace: Plotly.Data = {
        type: "scatter",
        mode: "markers",
        name: trace.traceName,
        x: trace.xValues,
        y: trace.yValues,
        marker: {
          size: trace.zValues?.length ? trace.zValues : undefined,
          color: trace.colorValues?.length ? trace.colorValues : undefined,
          showscale: !!trace.colorValues?.length,
          colorbar: {
            title: data.colorAxisName,
          },
        },
        text: trace.labels,
        hoverinfo: "text",
      };

      traces.push(bubbleTrace);

      if (props.trendLines && trace.xValues && trace.yValues) {
        const x = trace.xValues;
        const y = trace.yValues;

        const minX = Math.min(...x);
        const maxX = Math.max(...x);

        const trendlineTrace = addTrendline(x, y, schemeCategory10[index % 10], trace.traceName ?? "", minX, maxX);

        if (trendlineTrace) traces.push(trendlineTrace as Plotly.Data);
      }
    });

    return traces;
  };

  const getChartLayout = (width: number): Partial<Layout> => {
    const adjustedLayout = props.bubbleChart
      ? {
          ...layoutConfig,
          margin: {
            ...layoutConfig.margin,
            b: 45,
          },
        }
      : layoutConfig;

    return {
      ...axesConfig,
      ...adjustedLayout,
      width,
      height: props.height as number,
      images: [
        {
          layer: "below",
          yanchor: "bottom",
          sizing: "stretch",
          xref: "x",
          yref: "y",
          ...createPhotoConfig(props.imageConfig),
        },
      ],
      annotations: [...createAnnotationsConfig(props.annotationsConfig)],
    };
  };

  return (
    <div>
      {props.bubbleChart && !bubbleChartData ? (
        <span>
          <ProgressSpinner
            style={{ width: "2.5rem", height: "2.5rem" }}
            strokeWidth="4"
            fill="var(--surface-ground)"
            animationDuration=".5s"
          />
        </span>
      ) : (
        <ChartWrapper
          title=""
          width={props.width}
          uniqueKey={uniqueId}
          identifiers={props.bubbleChart ? [] : flattenArrayOfObjects()}
          showDateSelection={props.showDateSelection ?? true}
          promiseHandler={promiseHandler}
          updateFrequency={props.updateFrequency}
          reportMode={props.reportMode}
          height={props.height}
          dateFrom={timeState.from}
          dateTo={timeState.to}
          chartConfig={chartConfig}
          loadConf={props.loadConf}
          hideRefresh={props.hideRefresh}
          referenceTime={props.referenceTime}
          dynamicHeight={props.dynamicHeight}
          hideHeader={props.hideHeader}
          withoutIdentifiers={props.bubbleChart}
        >
          {(wrapperProps: any) => (
            <>
              {titlesDialog && (
                <DatasetsTitlesShower
                  dialog={titlesDialog}
                  setDialog={setTitlesDialog}
                  titlesAndOverriddenTitles={titlesAndOverriddenTitles}
                />
              )}
              {exportCsvDialog && (
                <ExportCsvDialog
                  dialog={exportCsvDialog}
                  setDialog={setExportCsvDialog}
                  data={wrapperProps.data}
                  chartType="SCATTER"
                />
              )}
              <div>
                <Plot
                  data={props.bubbleChart && bubbleChartData ? handleBubbleChartData(bubbleChartData) : [...wrapperProps.data]}
                  className="ea-plotly-scatter-chart"
                  config={{ ...wrapperProps.chartConfig }}
                  layout={getChartLayout(getChartSizeValue(props.width || wrapperProps.width))}
                  onInitialized={() => setStatus({ state: "finished" })}
                />

                {!props.reportMode &&
                  props.discreteColorMode &&
                  props.colorAxisIdentifier &&
                  props.discreteValues &&
                  props.discreteValues.length > 0 && (
                    <div className="flex flex-wrap justify-content-center gap-3 mt-3">
                      {props.discreteValues.map(({ key, value, title }) => (
                        <div key={key} className="flex items-center gap-2 whitespace-nowrap">
                          <span className="w-1rem h-1rem border-circle inline-block" style={{ backgroundColor: value }} />
                          <span className="text-sm">{I(title)}</span>
                        </div>
                      ))}
                    </div>
                  )}
              </div>
            </>
          )}
        </ChartWrapper>
      )}
    </div>
  );
}
